<template>
  <div class="single-box">
    <router-link class="single-box__link" :to="product.permalink">
      <div v-if="!hideImage">
        <Gallery
          :thumbnail="product.thumbnail"
          :displayType="'thumbnail'"
          :canOpen="false"
        />
      </div>

      <div v-if="!hideName" class="single-box__name">
        <Badges
          :is_on_sale="product.is_on_sale"
          :is_new="product.is_new"
          :is_out_of_stock="product.stock_status != 'instock'"
          :discount_percentage="product.discount_percentage || 0"
        />
        <h6 class="product-name">{{ product.name | emptyDash }}</h6>
      </div>

      <div v-if="!hideSKU">
        <div class="shop-table__line">
          {{ product.sku | emptyDash }}
        </div>
      </div>
    </router-link>

    <div v-if="multipleAttributes" class="single-box__multiple-attributes">
      <ul>
        <li v-for="attribute in multipleAttributes" :key="attribute.label">
          <span class="single-box__multiple-attributes__attribute-label"
            >{{ attribute.label }}:
          </span>
          <span
            class="single-box__multiple-attributes__attribute-value"
            v-html="attribute.content"
          ></span>
        </li>
      </ul>
    </div>

    <div class="single-box__price">
      <Price
        v-if="product.meta_fields._directo_product_closed != 1"
        :isVariable="product.is_variable"
        :showPriceRange="this.show_variation_max_price"
        :regularPrice="getVariantRegularPrice"
        :salePrice="getVariantSalePrice"
      />
    </div>

    <div class="single-box__wishlist">
      <Wishlist :productId="product.id" />
    </div>

    <div
      class="single-box__add-cart"
      v-if="getProductButtonType === 'add_to_cart'"
    >
      <div class="add-to-cart add-to-cart--variations">
        <router-link class="single-box__link" :to="product.permalink">
          •••
        </router-link>
      </div>
    </div>

    <div
      class="single-box__view-product"
      v-if="getProductButtonType === 'view_product'"
    >
      <router-link
        class="single-box__view-product__link"
        :to="product.permalink"
      >
        <button>{{ translations.view_product_label }}</button>
      </router-link>
    </div>
  </div>
</template>

<style scoped lang="scss">
.single-box {
  display: grid;
  grid-template-columns: repeat(3, 1r);

  &__link {
    text-decoration: none;
  }
}
</style>

<script>
import Badges from '@/layouts/general-ui/Badges.vue';
import Price from '@/layouts/general-ui/Price.vue';
import Gallery from '@/layouts/general-ui/Gallery.vue';
import Wishlist from '@/layouts/general-ui/Wishlist.vue';

export default {
  name: 'VariationBox',
  components: {
    Badges,
    Price,
    Gallery,
    Wishlist,
  },
  props: {
    product: Object,
    hideImage: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideName: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideSKU: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      show_variation_max_price: ws_vue_store.show_variation_max_price === '1',
      separator: ws_vue_store.separate_attributes === '1' ? ', ' : ' ',
      display_instock_variations:
        ws_vue_store.display_instock_variations === '1',
      translations: {
        readMore: ws_vue_store.translations.read_more,
        view_product_label: ws_vue_store.translations.view_product_label,
        available_variations: ws_vue_store.translations.available_variations,
      },
    };
  },
  filters: {
    emptyDash(value) {
      if (!value) return '-';
      return value;
    },
  },
  computed: {
    getVariantRegularPrice() {
      if (this.show_variation_max_price) {
        return this.product.max_price;
      }
      return this.product.regular_price;
    },
    getVariantSalePrice() {
      if (this.show_variation_max_price) {
        return this.product.min_price;
      }
      return this.product.sale_price;
    },
    getProductButtonType() {
      if (ws_vue_store.config.product_button === 'add_to_cart') {
        return 'add_to_cart';
      }
      if (ws_vue_store.config.product_button === 'view_product') {
        return 'view_product';
      }

      return '';
    },
    productPermalink() {
      return this.product.permalink.substring(
        this.product.permalink.indexOf(ws_vue_store.base_url) +
          ws_vue_store.base_url.length -
          1
      );
    },
    sortedVariations() {
      if (this.$store.state.view.sort.code === 'price_low_to_high') {
        return this.product.variations
          .slice(0)
          .sort((first, second) => first.sale_price - second.sale_price);
      }
      if (this.$store.state.view.sort.code === 'price_high_to_low') {
        return this.product.variations
          .slice(0)
          .sort((first, second) => second.sale_price - first.sale_price);
      }
      return this.product.variations;
    },
    multipleAttributes() {
      // Show only instock variations
      if (this.display_instock_variations) {
        if (!this.product.variations) {
          return false;
        }

        // Get product in-stock variation names
        let stockedVariations = [];
        this.product.variations.forEach(function (variation) {
          if (variation.stock_status == 'instock') {
            stockedVariations.push(variation.name);
          }
        });

        if (stockedVariations) {
          return [
            {
              label: this.translations.available_variations,
              content: stockedVariations
                .map(
                  (name) =>
                    `<span class="single-box__multiple-attributes__attribute-value__option">${name}</span>`
                )
                .join(this.separator),
            },
          ];
        }
        return false;
      }

      // Show product attributes
      if (
        ws_vue_store.config.kaali_allowed_attributes === undefined ||
        ws_vue_store.config.kaali_allowed_attributes.length <= 1
      )
        return false;

      let attributes = this.product.attributes.filter((attr) =>
        ws_vue_store.config.kaali_allowed_attributes.includes(attr.key)
      );
      if (attributes) {
        let processed = [];

        for (const attr in attributes) {
          processed.push({
            label: attributes[attr].label,
            content: attributes[attr].options
              .map(
                (t) =>
                  `<span class="single-box__multiple-attributes__attribute-value__option">${t.name}</span>`
              )
              .join(this.separator),
          });
        }
        return processed;
      }

      return false;
    },
  }, // computed
  methods: {},
};
</script>
